import type { ComponentType } from 'react';
import type { XMenuItem, XRole, XRoute } from './module-types';
import InventoryDashboard from '../screens/domain/dashboard/InventoryDashboard';

/**
 * THIS PRODUCT's navigation — inventory management.
 *
 * ---------------------------------------------------------------------------
 * STARTING A NEW PROJECT
 * ---------------------------------------------------------------------------
 * Replace this file. Everything else in config/ is platform: roles, the module
 * shells per role, dashboard/profile/settings/my-organizations. Those stay.
 *
 * Both exports may be empty — a product with no domain screens still gets a
 * working app (dashboard, profile, settings, org switching, user management).
 *
 * Matching pieces to update elsewhere:
 *   - screens/registry.tsx        the screen key -> component map
 *   - services .../domain.registry.ts   the API modules behind these screens
 *   - mobileapp .../domain-routes.ts    the same list for mobile
 * ---------------------------------------------------------------------------
 *
 * `screen` is a key into the screen registry, not an import — that is what
 * keeps this file pure data and free of heavy component imports.
 */

/** Route table: every domain page, list and detail. */
export const domainRoutes: XRoute[] = [
  { id: 'products', path: '/products', screen: 'products', title: 'Products', pageType: 'no-filter-list' },
  { id: 'warehouses', path: '/warehouses', screen: 'warehouses', title: 'Warehouses', pageType: 'no-filter-list' },
  { id: 'stock-levels', path: '/stock-levels', screen: 'stock-levels', title: 'Stock Levels', pageType: 'no-filter-list' },
  { id: 'stock-adjustments', path: '/stock-adjustments', screen: 'stock-adjustments', title: 'Stock Adjustments', pageType: 'no-filter-list' },
  { id: 'categories', path: '/categories', screen: 'categories', title: 'Categories', pageType: 'no-filter-list' },
  { id: 'customers', path: '/customers', screen: 'customers', title: 'Customers', pageType: 'no-filter-list' },
  { id: 'factories', path: '/factories', screen: 'factories', title: 'Factories', pageType: 'no-filter-list' },
  { id: 'brands', path: '/brands', screen: 'brands', title: 'Brands', pageType: 'no-filter-list' },
  { id: 'suppliers', path: '/suppliers', screen: 'suppliers', title: 'Suppliers', pageType: 'no-filter-list' },
  { id: 'orders', path: '/orders', screen: 'orders', title: 'Purchase Orders', pageType: 'no-filter-list' },
  { id: 'sales', path: '/sales', screen: 'sales', title: 'Sales', pageType: 'no-filter-list' },
  { id: 'invoices', path: '/invoices', screen: 'invoices', title: 'Invoices', pageType: 'no-filter-list' },
  { id: 'payments', path: '/payments', screen: 'payments', title: 'Payments', pageType: 'no-filter-list' },
];

/**
 * The collapsible submenu these pages live under, shared by Owner/Admin/Manager.
 * Null means "this product has no domain submenu" — the platform config skips
 * it rather than rendering an empty section.
 */
export const domainMenu: XMenuItem | null = {
  id: 'inventory',
  label: 'Inventory',
  path: '/products',
  icon: 'box',
  children: [
    { id: 'products', label: 'Products', path: '/products', pageType: 'table', icon: 'box' },
    { id: 'warehouses', label: 'Warehouses', path: '/warehouses', pageType: 'table', icon: 'warehouse' },
    { id: 'stock-levels', label: 'Stock Levels', path: '/stock-levels', pageType: 'table', icon: 'box' },
    { id: 'stock-adjustments', label: 'Stock Adjustments', path: '/stock-adjustments', pageType: 'table', icon: 'clipboard' },
    { id: 'categories', label: 'Categories', path: '/categories', pageType: 'table', icon: 'list' },
    { id: 'customers', label: 'Customers', path: '/customers', pageType: 'table', icon: 'users' },
    { id: 'factories', label: 'Factories', path: '/factories', pageType: 'table', icon: 'building' },
    { id: 'brands', label: 'Brands', path: '/brands', pageType: 'table', icon: 'tasks' },
    { id: 'suppliers', label: 'Suppliers', path: '/suppliers', pageType: 'table', icon: 'workflow' },
    { id: 'orders', label: 'Orders', path: '/orders', pageType: 'table', icon: 'clipboard' },
    { id: 'sales', label: 'Sales', path: '/sales', pageType: 'table', icon: 'report' },
    { id: 'invoices', label: 'Invoices', path: '/invoices', pageType: 'table', icon: 'forms' },
    { id: 'payments', label: 'Payments', path: '/payments', pageType: 'table', icon: 'review' },
  ],
};

/**
 * The dashboard each role lands on, when this product provides one.
 *
 * The platform owns the *routing* (screens/dashboard/home.tsx dispatches by
 * role) but not the *content*, which is inevitably about the product's own
 * data — these three all read products, orders and sales. Any role left out
 * here falls back to PlatformHome, so an empty object is valid and a fresh
 * project still has a working landing page.
 *
 * 'platform owner' is deliberately absent: that dashboard reports on
 * organizations and revenue, which is platform data, so it stays in
 * screens/dashboard/role/PlatformOwnerHome.tsx.
 */
export const domainRoleHomes: Partial<Record<XRole, ComponentType>> = {
  owner: InventoryDashboard,
  admin: InventoryDashboard,
  manager: InventoryDashboard,
};
